import {
  CUSTOM_ELEMENTS_SCHEMA,
  ChangeDetectionStrategy,
  Component,
  ElementRef,
  EventEmitter,
  Input,
  OnChanges,
  Output,
  inject,
  signal,
} from '@angular/core';

import { translate, translateObject, TranslocoModule, TranslocoService } from '@jsverse/transloco';

import { MarkdownModule } from 'ngx-markdown';
import { filter, tap } from 'rxjs';

import { AppSharedModule } from 'app/app-shared.module';
import { SimpleDialogButtonColor } from '../../../../../../components/simple-dialog.component';
import { AiInsight } from '../../../../../../providers/semantria-api/ai-insights.service';
import { AlertService } from '../../../../../../services/alert.service';
import { DialogService } from '../../../../../../services/dialog.service';
import {
  EnableClassTelemetry,
  EnableComponentTelemetry,
  injectComponentTelemetryService,
} from '../../../../../../services/otel.service';

import { PublishInsightDialogComponent } from '../../../publish-insight-dialog/publish-insight-dialog.component';
import { InsightCardModalComponent } from './insight-card-modal/insight-card-modal.component';
import { InsightCardFacade } from './insight-card.facade';

export interface InsightCardOptions {
  canGenerateInsights: boolean;
  isReadOnly: boolean;
  currentUserId?: string;
}

interface PublishInsightPayload {
  insight: AiInsight;
  labels: unknown[];
}

export interface AiInsightCardModel {
  id: string;
  shortPurpose: string;
  longPurpose: string;
  createdAt: Date;
  summary: any;
  aiInsight?: any;
  isGenerating: boolean;
  isCompleted?: boolean;
  error?: string;
  isExpanded: boolean;
  published: boolean;
  tags: string[];
  comments?: string;
}

const parseTagLabels = (tags: string[] | undefined): string[] => {
  if (!tags?.length) {
    return [];
  }

  return tags
    .map((tag) => {
      if (!tag) {
        return undefined;
      }

      try {
        const parsed = JSON.parse(tag) as { label?: string };
        return parsed?.label || tag;
      } catch {
        return tag;
      }
    })
    .filter((label): label is string => !!label);
};

const mapAiInsightToCardModel = (insight: AiInsight, isExpanded = false): AiInsightCardModel => {
  return {
    id: insight.insight_id,
    longPurpose: insight.properties.longPurpose as string,
    shortPurpose: insight.properties.shortPurpose as string,
    isGenerating: insight.insight_status === 'IN_PROGRESS' || insight.insight_status === 'QUEUED',
    isCompleted: insight.insight_status === 'COMPLETED',
    aiInsight: insight.localizations?.[0]?.text ?? '',
    summary: insight.properties.summary,
    createdAt: new Date(insight.creation_date),
    error: insight.error_message,
    isExpanded,
    published: !!insight.published,
    tags: parseTagLabels(insight.tags),
    comments: insight.comment,
  };
};

@EnableClassTelemetry()
@Component({
  selector: 'app-insight-card',
  imports: [
    AppSharedModule,
    MarkdownModule,
    PublishInsightDialogComponent,
    InsightCardModalComponent,
    TranslocoModule,
  ],
  schemas: [CUSTOM_ELEMENTS_SCHEMA],
  templateUrl: './insight-card.component.html',
  styleUrl: './insight-card.component.scss',
  providers: [InsightCardFacade],
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class InsightCardComponent implements OnChanges {
  @EnableComponentTelemetry() otelService = injectComponentTelemetryService();

  @Input() insight: AiInsight;
  @Input() isReadOnly: boolean;
  @Input() insightFeed = false;
  @Input() selectedForDownload = false;
  @Input() options: InsightCardOptions = {
    canGenerateInsights: false,
    isReadOnly: true,
    currentUserId: undefined,
  };
  @Output() insightDeleted = new EventEmitter();
  @Output() regenerateInsight = new EventEmitter();
  @Output() publishInsight = new EventEmitter<PublishInsightPayload>();
  @Output() selectionChange = new EventEmitter<{ insight: AiInsight; selected: boolean }>();

  insightCardModel: AiInsightCardModel;
  isDeleting = signal(false);
  showPublishModal = signal(false);
  showInsightFeedViewModal = signal(false);
  deleteTooltip?: { text: string };
  canUpdateInsights: boolean;
  deleteDisabled = true;

  private dialogService = inject(DialogService);
  private readonly elementRef = inject(ElementRef) as ElementRef<HTMLElement>;
  private readonly alertService = inject(AlertService);
  private readonly transloco = inject(TranslocoService);
  readonly facade = inject(InsightCardFacade);
  private reopenInsightFeedAfterPublish = false;

  ngOnChanges(): void {
    //TODO: the insight needs to be updated for handling different languages
    if (this.insight) {
      this.canUpdateInsights =
        this.options.canGenerateInsights && !this.options.isReadOnly && !this.isReadOnly;

      this.facade.onInsightChanged(this.insight);
      if (!this.insightFeed) {
        this.deleteDisabled = !this.canDeleteInsight(this.insight);
        this.deleteTooltip = this.deleteDisabled
          ? { text: translate('widgets.ai-insights.delete-disabled-user') }
          : undefined;
        this.isDeleting.set(false);
      }
      const isExpanded = this.insightCardModel?.isExpanded ?? false;
      this.insightCardModel = mapAiInsightToCardModel(this.insight, isExpanded);
    }
  }

  get publishedAt(): Date | null {
    if (!this.insight?.published) {
      return null;
    }

    const ts = this.insight.modification_date || this.insight.creation_date;
    if (!ts) {
      return null;
    }

    const d = new Date(ts);
    return Number.isNaN(d.getTime()) ? null : d;
  }

  deleteInsight() {
    if (this.deleteDisabled) {
      return;
    }

    this.isDeleting.set(true);

    const translations = translateObject('widgets.ai-insights.insight-card.delete-popup');
    this.dialogService
      .showSimpleDialog({
        title: translations['title'],
        content: translations['content'],
        confirmTitle: translations['ok'],
        cancelTitle: translations['cancel'],
        confirmColor: SimpleDialogButtonColor.red,
      })
      .afterClosed()
      .pipe(
        tap((result) => {
          if (!result) {
            this.isDeleting.set(false);
          }
        }),
        filter((result) => !!result)
      )
      .subscribe(() => this.insightDeleted.emit());
  }

  regenerateInsightClicked() {
    this.regenerateInsight.emit();
  }

  async copyInsight(): Promise<void> {
    const host = this.elementRef.nativeElement;
    const startEl = host.querySelector('.insight-card__title');
    if (!startEl) {
      return;
    }

    const endBoundary = host.querySelector('.insight-card__expand');

    const cloneRange = document.createRange();
    cloneRange.setStartBefore(startEl);
    if (endBoundary) {
      cloneRange.setEndBefore(endBoundary);
    } else {
      const last = host.lastElementChild ?? startEl;
      cloneRange.setEndAfter(last);
    }
    const fragment = cloneRange.cloneContents();
    const container = document.createElement('div');
    container.appendChild(fragment);
    container.querySelectorAll('h1,h2,h3,h4,h5,h6').forEach((heading) => {
      const div = document.createElement('div');
      while (heading.firstChild) {
        div.appendChild(heading.firstChild);
      }
      heading.replaceWith(div);
    });

    const html = container.innerHTML;
    const text = container.textContent ?? '';
    if (!navigator.clipboard || typeof ClipboardItem === 'undefined') {
      return;
    }

    await navigator.clipboard.write([
      new ClipboardItem({
        'text/html': new Blob([html], { type: 'text/html' }),
        'text/plain': new Blob([text], { type: 'text/plain' }),
      }),
    ]);

    this.alertService.showToast(
      this.transloco.translate('widgets.ai-insights.insight-card.copied')
    );
  }

  onSelectionChange(selected: boolean) {
    if (!this.insight) {
      return;
    }
    this.selectionChange.emit({ insight: this.insight, selected });
  }

  showPublishInsightModal() {
    void this.facade.ensureInsightImageLoaded(this.insight);
    this.showPublishModal.set(true);
  }

  onPublishInsightModalClosed(result: { insight: AiInsight; labels: unknown[] } | null) {
    this.showPublishModal.set(false);

    if (result?.insight) {
      this.applyInsightUpdate(result.insight);
      this.publishInsight.emit(result);
    }

    if (this.reopenInsightFeedAfterPublish) {
      this.reopenInsightFeedAfterPublish = false;
      this.showInsightFeedViewModal.set(true);
    }
  }

  async openInsightFeed() {
    this.showInsightFeedViewModal.set(true);

    await this.facade.ensureInsightImageLoaded(this.insight);
  }

  onPublishOrViewClicked() {
    if (this.insight?.published) {
      this.openInsightFeed();
      return;
    }

    this.showPublishInsightModal();
  }

  onEditInsightClicked(_insightCardModel?: AiInsightCardModel) {
    this.reopenInsightFeedAfterPublish = true;
    this.showInsightFeedViewModal.set(false);
    this.showPublishInsightModal();
  }

  private applyInsightUpdate(result: AiInsight): void {
    if (!result) {
      return;
    }

    const isExpanded = this.insightCardModel?.isExpanded ?? false;
    this.insight = result;
    this.insightCardModel = mapAiInsightToCardModel(result, isExpanded);
  }

  onInsightFeedViewModalClosed() {
    this.showInsightFeedViewModal.set(false);
  }

  private canDeleteInsight(insight: AiInsight): boolean {
    // Don't allow deletion if no permissions
    if (!this.options?.canGenerateInsights) {
      return false;
    }

    // Don't allow deletion if insight is generating
    if (insight.insight_status === 'IN_PROGRESS' || insight.insight_status === 'QUEUED') {
      return false;
    }

    // Check read-only mode and creator permissions
    if (this.options.isReadOnly) {
      return insight.creator_user_id === this.options.currentUserId;
    }

    return true;
  }
}
